import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, Building, Phone, Mail } from 'lucide-react';

const offices = [
  {
    country: "India",
    flagUrl: "https://flagcdn.com/w80/in.png",
    city: "New Delhi",
    address: "109/2/1A, Street No. 14A, West Azad Nagar, Krishna Nagar, New Delhi -110051",
    type: "Headquarters",
    color: "from-orange-500 to-green-500"
  },
  {
    country: "Hong Kong",
    flagUrl: "https://flagcdn.com/w80/hk.png",
    city: "Hong Kong SAR",
    address: "Regional Trade Office",
    type: "Regional Office",
    color: "from-red-500 to-pink-500"
  },
  {
    country: "UAE",
    flagUrl: "https://flagcdn.com/w80/ae.png",
    city: "Dubai",
    address: "Middle East Operations",
    type: "Regional Office",
    color: "from-green-500 to-white"
  }
];

const GlobalPresence = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="global" className="py-20 md:py-32 relative overflow-hidden" data-testid="global-section">
      {/* Background Map */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1713098965471-d324f294a71d?w=1920&q=80"
          alt="World Map"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark via-brand-dark/95 to-brand-dark" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-brand-primary/20 text-brand-accent text-sm font-medium mb-4">
            Global Presence
          </span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Connecting <span className="gradient-text">Three Continents</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Strategic locations in Asia and Middle East for seamless international trade
          </p>
        </motion.div>

        {/* Office Cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {offices.map((office, index) => (
            <motion.div
              key={office.country}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="group relative"
              data-testid={`office-card-${office.country.toLowerCase()}`}
            >
              {/* Card */}
              <div className="p-6 md:p-8 rounded-2xl bg-brand-surface/70 border border-white/5 hover:border-brand-primary/30 transition-all duration-300 h-full">
                {/* Flag & Country */}
                <div className="flex items-center gap-3 mb-6">
                  <img 
                    src={office.flagUrl} 
                    alt={`${office.country} flag`}
                    className="w-10 h-7 object-cover rounded shadow-sm"
                  />
                  <div>
                    <h3 className="font-outfit text-xl font-bold text-white">{office.country}</h3>
                    <span className="text-sm text-brand-accent">{office.type}</span>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-white">{office.city}</div>
                    <div className="text-sm text-gray-400">{office.address}</div>
                  </div>
                </div>

                {/* Glow effect on hover */}
                <div className={`absolute -inset-px rounded-2xl bg-gradient-to-r ${office.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl`} />
              </div>

              {/* Connection Line */}
              {index < offices.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-brand-primary/50 to-transparent" />
              )}
            </motion.div>
          ))}
        </div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-16 flex flex-wrap justify-center gap-8"
        >
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-10 h-10 rounded-full bg-brand-primary/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-brand-accent" />
            </div>
            <span>info.ajktv@gmail.com</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-10 h-10 rounded-full bg-brand-primary/20 flex items-center justify-center">
              <Building className="w-5 h-5 text-brand-accent" />
            </div>
            <span>Est. 2018</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalPresence;
